<template>
  <ValidationObserver ref="customerForm" v-slot="{ invalid, passes }">
    <v-form class="mb-3" autocomplete="off" @submit.prevent="passes(downloadReport)">
      <v-row class="filters" align="center" justify="start">
        <v-col class="pl-6 pr-6 pt-6">
          <v-row>
            <v-col
              cols="12"
              sm="3"
              :class="`pb-0 filter-datepicker ${refDatePickerFrom}`"
            >
              <ValidationProvider name="since" rules="required|range:@since,@until"
                                  v-slot="{errors}">
                <v-text-field
                  v-model.trim="dateFromFormatted"
                  outlined
                  dense
                  background-color="white"
                  label="Export od"
                  append-icon="mdi-calendar-arrow-left"
                  :error-messages="errors"
                  @blur="filters.since = setParsedSince(dateFromFormatted)"
                  @click="menuForDateFrom = true"
                  @click:prepend="filters.since = null"
                  @click:append="menuForDateFrom = true"
                />
                <v-menu
                  ref="menuForDateFrom"
                  v-model="menuForDateFrom"
                  :attach="`.${refDatePickerFrom}`"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  max-width="290px"
                  min-width="290px"
                >
                  <v-date-picker
                    v-model="filters.since"
                    :first-day-of-week="1"
                    :max="maxForDateFrom"
                    no-title
                    @input="menuForDateFrom = false"
                  />
                </v-menu>
              </ValidationProvider>
            </v-col>
            <v-col
              cols="12"
              sm="3"
              :class="`pb-0 filter-datepicker ${refDatePickerTo}`"
            >
              <ValidationProvider name="until" rules="required|range:@since,@until"
                                  v-slot="{errors}">
                <v-text-field
                  v-model.trim="dateToFormatted"
                  outlined
                  dense
                  background-color="white"
                  label="Export do"
                  append-icon="mdi-calendar-arrow-right"
                  :error-messages="errors"
                  @blur="filters.until = setParsedUntil(dateToFormatted)"
                  @click="menuForDateTo = true"
                  @click:prepend="filters.until = null"
                  @click:append="menuForDateTo = true"
                />
                <v-menu
                  ref="menuForDateTo"
                  v-model="menuForDateTo"
                  :attach="`.${refDatePickerTo}`"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <v-date-picker
                    v-model="filters.until"
                    :first-day-of-week="1"
                    :max="maxForDateTo"
                    :min="minForDateTo"
                    no-title
                    @input="menuForDateTo = false"
                  />
                </v-menu>
              </ValidationProvider>
            </v-col>
            <v-col cols="12"
                   sm="4"
                   class="pb-0"
            >
              <v-checkbox
                v-model="filters.onlyChanges"
                class="mt-2 pt-0"
                dense
                hide-details
                label="Pouze změněné stavy"
                title="Vynechá záznamy shodné s předchozím zápisem"
              />
            </v-col>
            <v-col cols="12"
                   sm="2"
                   class="pb-3 text-right"
            >
              <v-btn
                depressed
                :loading="loading"
                type="submit"
                height="40px"
                min-width="130px"
                color="blue-grey"
                class="white--text"
              >
                Export
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </ValidationObserver>
</template>

<script>
import parseDate from '@/utils/parseDate';
import { downloadFile } from '@/components/mixins/downloadFile';
import { setFilters } from '@/components/mixins/setFilters';

export default {
  name: 'ExportData',
  props: {
    type: {
      required: true,
    },
  },
  data() {
    return {
      dateFromFormatted: null,
      dateToFormatted: null,
      menuForDateFrom: false,
      menuForDateTo: false,
      filters: {
        since: null,
        until: null,
        type: this.type,
        onlyChanges: true,
      },
      loading: false,
    };
  },
  created() {
    this.filters.since = this.getDefaultSinceDate();
    this.filters.until = this.getDefaultUntilDate();
  },
  mixins: [downloadFile, setFilters],
  computed: {
    refDatePickerFrom() {
      return `${this.type}-filter-datepicker--from`;
    },
    refDatePickerTo() {
      return `${this.type}-filter-datepicker--to`;
    },
    maxForDateFrom() {
      if (this.filters.until) {
        return new Date(this.filters.until).toISOString();
      }
      return new Date().toISOString();
    },
    maxForDateTo() {
      return new Date().toISOString();
    },
    minForDateTo() {
      if (this.filters.since) {
        return new Date(this.filters.since).toISOString();
      }
      return null;
    },
  },
  watch: {
    'filters.since': {
      handler() {
        this.dateFromFormatted = this.formatDate(this.filters.since);
      },
    },
    'filters.until': {
      handler() {
        this.dateToFormatted = this.formatDate(this.filters.until);
      },
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year}`;
    },
    setParsedSince(dateFormatted) {
      this.filters.since = parseDate(dateFormatted);
      if (!this.filters.since) {
        this.dateFromFormatted = null;
      }
      return this.filters.since;
    },
    setParsedUntil(dateFormatted) {
      this.filters.until = parseDate(dateFormatted);
      if (!this.filters.until) {
        this.dateToFormatted = null;
      }
      return this.filters.until;
    },
    getDefaultSinceDate() {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return this.getDateISO(date);
    },
    getDefaultUntilDate() {
      return this.getDateISO(new Date());
    },
    getDateISO(date) {
      return date.toJSON().slice(0, 10).replace(/-/g, '-');
    },
    downloadReport() {
      if (this.filters.since == null || this.filters.until == null) return;
      this.loading = true;
      this.downloadFile(this.setFiltersExact(`printer/${this.$route.params.serialNumber}/export`, this.filters)).then(() => {
        this.loading = false;
      });
    },
  },
};
</script>
